import { initComponentStore, noop } from "@/utils/mobx";


const ALPHA_ROWS: string[][] = [
    ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
    ["Shift", "z", "x", "c", "v", "b", "n", "m", "Backspace"],
    ["123", ",", " ", ".", "Enter"],
];

const NUM_ROWS: string[][] = [
    ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
    ["-", "/", ":", ";", "(", ")", "$", "&", "@", '"'],
    [".", ",", "?", "!", "'", "_", "=", "Backspace"],
    ["ABC", " ", "Enter"],
];

// Keys that shouldn't auto-repeat (modifiers / layer switches).
const NO_REPEAT = new Set(["Shift", "123", "ABC"]);

// Delay before first repeat fires, then interval between repeats (ms).
const REPEAT_DELAY = 500;
const REPEAT_INTERVAL = 50;

type Layer = "alpha" | "num";

export class VirtualKeyboardStore {
    onKey: (key: string) => void = noop as any;
    onCursorMove: (pos: number) => void = noop as any;
    onClose: (() => void) | null = null;
    onHeightChange: ((height: number) => void) | null = null;
    visible: boolean = false;
    disableAutoClose: boolean = false;
    value: string = "";
    placeholder: string = "";
    cursorPos: number = 0;
    selStart: number = 0;
    selEnd: number = 0;
    showEmbedded: boolean = false;

    shifted: boolean = false;
    layer: Layer = "alpha";
    collapsed: boolean = false;
    enableSplitter: boolean = false;
    password: boolean = false;

    onVisibilityChange: ((visible: boolean) => void) | null = null;
    onCollapsedChange: ((collapsed: boolean) => void) | null = null;

    // Timer IDs — not reactive, excluded from MobX via overrides.
    _repeatDelay: ReturnType<typeof setTimeout> | null = null;
    _repeatInterval: ReturnType<typeof setInterval> | null = null;

    constructor() {
        initComponentStore(this, {
            _repeatDelay: false,
            _repeatInterval: false,
            onHeightChange: false,
            onVisibilityChange: false,
            onCollapsedChange: false,
        });
    }

    setCollapsed(value: boolean) {
        if (this.collapsed === value) return;
        this.collapsed = value;
        this.onCollapsedChange?.(value);
    }

    get rows(): string[][] {
        return this.layer === "alpha" ? ALPHA_ROWS : NUM_ROWS;
    }

    handleKey(key: string) {
        if (key === "Shift") { this.shifted = !this.shifted; return; }
        if (key === "123") { this.layer = "num"; return; }
        if (key === "ABC") { this.layer = "alpha"; this.shifted = false; return; }
        if (key.length === 1 && this.shifted && this.layer === "alpha") {
            this.onKey(key.toUpperCase());
            this.shifted = false;
            return;
        }
        this.onKey(key);
        if (key === "Enter") {
            if (!this.disableAutoClose) this.onClose?.();
            return;
        }
        // Return to alpha after picking a non-numeric character from the num layer.
        // Digits and Backspace stay on num so multiple numbers can be entered.
        if (this.layer === "num" && key !== "Backspace" && key !== " " && !/^\d$/.test(key)) {
            this.layer = "alpha";
        }
    }

    stopRepeat() {
        if (this._repeatDelay !== null) { clearTimeout(this._repeatDelay); this._repeatDelay = null; }
        if (this._repeatInterval !== null) { clearInterval(this._repeatInterval); this._repeatInterval = null; }
    }

    startRepeat(key: string) {
        if (NO_REPEAT.has(key)) return;
        this.stopRepeat();
        this._repeatDelay = setTimeout(() => {
            this._repeatInterval = setInterval(() => this.handleKey(key), REPEAT_INTERVAL);
        }, REPEAT_DELAY);
    }

    getLabel(key: string): string {
        if (key === "Backspace") return "⌫";
        if (key === " ") return "space";
        if (key === "Enter") return "↵";
        if (key === "Shift") return this.shifted ? "⇧" : "⬆";
        if (key.length === 1 && this.shifted && this.layer === "alpha") return key.toUpperCase();
        return key;
    }

    getKeyClass(key: string): string {
        const cls = ["vkb__key"];
        if (key === " ") cls.push("vkb__key--space");
        else if (key === "Enter") cls.push("vkb__key--enter");
        else if (key === "Backspace") cls.push("vkb__key--backspace");
        else if (key === "Shift") {
            cls.push("vkb__key--shift");
            if (this.shifted) cls.push("vkb__key--active");
        } else if (key === "123" || key === "ABC") cls.push("vkb__key--layer");
        return cls.join(" ");
    }
    
    applyVisible(value: boolean) {
        this.visible = value;
        if (!value) {
            this.stopRepeat();
        }
        this.onVisibilityChange?.(value);
    }

    dispose() {
        this.stopRepeat();
    }
}
